import { useState } from 'react';
import { IconWarn } from '../components/auth/AuthBits';
import { forgotPassword } from '../api';
import '../styles/page-login.css';
import Seo from '../components/Seo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function ForgotPasswordPage() {

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);

  const check = (val) => {
    if (!val.trim()) return 'Enter your work email.';
    if (!EMAIL_RE.test(val.trim())) return 'That email address looks incomplete.';
    return '';
  };

  /* validate on blur, not on every keystroke */
  const blur = (e) => setErr((s) => ({ ...s, email: check(e.target.value) }));

  const submit = (e) => {
    e.preventDefault();
    const bad = check(email);
    if (bad) {
      setErr({ email: bad });
      document.getElementById('fp-email')?.focus();
      return;
    }
    setBusy(true);
    setErr({});
    forgotPassword({ email })
      .then(() => setSent(true))
      .catch((e2) => {
        /* Never reveal whether an account exists: a "not found" is shown as sent. */
        if (e2?.status === 404) {
          setSent(true);
          return;
        }
        setErr({
          form:
            e2?.status === 429
              ? 'Too many attempts. Wait a few minutes and try again.'
              : e2?.message || 'Something went wrong. Please try again.',
        });
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Seo title="Reset Your Password | TeamGrid™" description="Reset the password for your TeamGrid workspace. We will email you a secure link to choose a new one." noindex />
<div className="auth-wrap">
  <div className="auth-card">
    <img className="amark" src="/assets/mark-teamgrid.svg" alt="TeamGrid" />

    {!sent && (
      <>
        <h1>Forgot your password?</h1>
        <p className="asub">No worries. Enter your work email and we&apos;ll send you a reset link.</p>
        <form data-fake onSubmit={submit} noValidate>
          <label htmlFor="fp-email">Work email</label>
          <input
            id="fp-email"
            required
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck="false"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (err.email) setErr((s) => ({ ...s, email: check(e.target.value) }));
            }}
            onBlur={blur}
            placeholder="you@company.com"
            aria-invalid={!!err.email}
            aria-describedby={err.email ? 'fp-email-err' : undefined}
          />
          {err.email && <p className="ax-err" id="fp-email-err" role="alert"><IconWarn />{err.email}</p>}

          {err.form && <p className="ax-err" role="alert" style={{ marginTop: '16px' }}><IconWarn />{err.form}</p>}

          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
        <p style={{fontSize: '12px', lineHeight: '18px', color: 'var(--ink-faint)', textAlign: 'center', marginTop: '14px'}}>
          The link expires in 30 minutes. Your existing password keeps working until you set a new one.
        </p>
      </>
    )}

    {sent && (
      <div className="oksplash show">
        <div className="okring">
          <svg aria-hidden="true" width="28" height="28" viewBox="0 0 16 16"><path fill="none" stroke="#1d7a43" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
        </div>
        <h3 style={{fontSize: '20px', fontWeight: '800', color: 'var(--ink-strong)'}}>Check your email</h3>
        <p style={{fontSize: '13.5px', color: 'var(--ink-soft)', marginTop: '8px'}}>
          We&apos;ve sent a password reset link to{' '}
          <span style={{color: 'var(--teal-deep)', fontWeight: '700'}}>{email || 'your inbox'}</span>.
        </p>
        <div style={{textAlign: 'left', background: '#f6fafa', border: '1px solid var(--line)', borderRadius: '12px', padding: '16px 18px', marginTop: '20px'}}>
          <div style={{fontSize: '11px', fontWeight: '800', letterSpacing: '.8px', color: 'var(--ink-faint)', marginBottom: '8px'}}>NEXT STEPS</div>
          <ul style={{listStyle: 'none', margin: 0, padding: 0, fontSize: '13px', lineHeight: '22px', color: 'var(--ink)'}}>
            <li>→ Open your inbox</li>
            <li>→ Look for the email from TeamGrid</li>
            <li>→ Click the reset link inside</li>
            <li>→ Create your new password</li>
          </ul>
        </div>
        <p style={{fontSize: '12.5px', color: 'var(--ink-soft)', marginTop: '16px'}}>
          Didn&apos;t get it? Check your spam folder, or{' '}
          <button
            type="button"
            onClick={() => { setSent(false); setErr({}); }}
            style={{background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--teal-deep)', fontWeight: '700', cursor: 'pointer', textDecoration: 'underline'}}
          >
            try a different address
          </button>.
        </p>
      </div>
    )}

    <p className="aswitch">Remembered it? <a href="/login">Back to sign in</a></p>
    <p style={{fontSize: '11.5px', color: 'var(--ink-faint)', textAlign: 'center', marginTop: '10px'}}>
      Still stuck? Email <a href="mailto:support@teamgrid.ai" style={{color: 'var(--teal-deep)', fontWeight: '700'}}>support@teamgrid.ai</a>
    </p>
  </div>
</div>
    </>
  );
}
